import { useState, useMemo, useEffect } from "react";
import { Outlet, Navigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sidebar } from "./sidebar";
import { useAuth } from "@/hooks/use-auth";
import { useWaiterCalls } from "@/hooks/use-waiter-calls";
import { WaiterCallPanel } from "@/components/waiter-call-panel";
import { OrderNotificationPanel } from "@/components/order-notification-panel";
import { getVenues, getMe } from "@/api/endpoints";
import { unlockAudio } from "@/utils/notification-sounds";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export function AppLayout() {
  const { isAuthenticated, logout, isStaff, staffRole, staffVenueId, staffInfo } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const qc = useQueryClient();

  // Verify user exists — if not, interceptor handles 401 → logout
  useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: isAuthenticated && !isStaff,
    retry: false,
  });

  // Get venue IDs for socket rooms
  const { data: venues } = useQuery({
    queryKey: ["venues"],
    queryFn: getVenues,
    enabled: isAuthenticated,
  });

  const venueIds = useMemo(
    () => (venues || []).map((v) => v.id || (v as unknown as Record<string, string>)._id).filter(Boolean),
    [venues]
  );

  const waiterSoundKey = useMemo(
    () => venues?.[0]?.notificationSounds?.waiterSound || "bell",
    [venues]
  );

  const orderSoundKey = useMemo(
    () => venues?.[0]?.notificationSounds?.orderSound || "kaching",
    [venues]
  );

  const currentPlanType = useMemo(
    () => venues?.[0]?.planType || "FREE",
    [venues]
  );

  const venueLogo = venues?.[0]?.logo;
  const venueName = venues?.[0]?.name;

  const { calls, dismissCall, dismissAll, newOrders, dismissOrder: rawDismissOrder, dismissAllOrders: rawDismissAllOrders, requestNotificationPermission } =
    useWaiterCalls(venueIds, waiterSoundKey, orderSoundKey);

  const dismissOrder = (id: string) => {
    rawDismissOrder(id);
    for (const vid of venueIds) qc.invalidateQueries({ queryKey: ["tables", vid] });
  };
  const dismissAllOrders = () => {
    rawDismissAllOrders();
    for (const vid of venueIds) qc.invalidateQueries({ queryKey: ["tables", vid] });
  };

  // Invalidate orders + tables query when new order arrives
  useEffect(() => {
    if (newOrders.length > 0) {
      for (const vid of venueIds) {
        qc.invalidateQueries({ queryKey: ["orders", vid] });
        qc.invalidateQueries({ queryKey: ["tables", vid] });
      }
    }
  }, [newOrders.length]);

  // Unlock audio on first user interaction & request notification permission
  useEffect(() => {
    const handler = () => { unlockAudio(); window.removeEventListener("click", handler); };
    window.addEventListener("click", handler);
    requestNotificationPermission();
    return () => window.removeEventListener("click", handler);
  }, []);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isStaff && staffVenueId && window.location.pathname === "/") {
    // Staff yeni 5-grup yapısında Sipariş section'ına gider (index = Masalar)
    return <Navigate to={`/venues/${staffVenueId}/orders`} replace />;
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <Sidebar onLogout={logout} planType={currentPlanType} venueLogo={venueLogo} venueName={isStaff ? staffInfo?.venueName : venueName} staffRole={staffRole} />
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 md:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <Sidebar onLogout={logout} onNavigate={() => setMobileOpen(false)} planType={currentPlanType} venueLogo={venueLogo} venueName={isStaff ? staffInfo?.venueName : venueName} staffRole={staffRole} />
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="flex h-14 items-center gap-3 border-b bg-card px-4 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </Button>
          <div className="flex items-center gap-2">
            <div className="flex size-7 items-center justify-center rounded-lg bg-primary">
              <span className="text-sm font-bold text-primary-foreground">H</span>
            </div>
            <span className="font-bold">HereMenu</span>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {/* Notifications */}
      <div className="fixed right-4 top-16 z-50 flex flex-col gap-3 md:right-6 md:top-4">
        <WaiterCallPanel
          calls={calls}
          onDismiss={dismissCall}
          onDismissAll={dismissAll}
          onRequestPermission={requestNotificationPermission}
        />
        <OrderNotificationPanel
          orders={newOrders}
          onDismiss={dismissOrder}
          onDismissAll={dismissAllOrders}
        />
      </div>
    </div>
  );
}
